/**
 * Onboarding Portal — Plans List (Option B)
 *
 * Shows the customer contact's granted onboarding plans with progress.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { onboardingPortalService, type PortalPlan, type PortalUser } from '@/services/api/onboardingPortal';
import {
  ClipboardList, Loader2, LogOut, User, ChevronRight, Calendar,
  Target, CheckCircle, AlertTriangle, Clock,
} from 'lucide-react';

const healthColors: Record<string, string> = {
  green: 'bg-green-100 text-green-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  red: 'bg-red-100 text-red-700',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft', active: 'Active', on_hold: 'On Hold', completed: 'Completed', churned: 'Churned',
};

export default function OnboardingPortalPlansPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PortalPlan[]>([]);
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!onboardingPortalService.isLoggedIn()) {
      navigate('/onboarding-portal/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [plansData, me] = await Promise.all([
        onboardingPortalService.getPlans(),
        onboardingPortalService.getMe(),
      ]);
      setPlans(plansData);
      setUser(me);
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        onboardingPortalService.logout();
        navigate('/onboarding-portal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    onboardingPortalService.logout();
    navigate('/onboarding-portal/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                Onboarding Portal
              </span>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user?.name || user?.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.name}</span>
                    <span className="text-xs text-gray-500">{user?.email}</span>
                    {user?.org_name && <span className="text-xs text-gray-400">{user.org_name}</span>}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Your Onboarding Plans</h1>

        {plans.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No onboarding plans have been shared with you yet.</p>
              <p className="text-sm text-gray-400 mt-1">Contact your account manager for access.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {plans.map(plan => (
              <Card
                key={plan.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/onboarding-portal/plans/${plan.id}`)}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                          {plan.name || 'Untitled Plan'}
                        </h3>
                        <Badge className={healthColors[plan.health] || 'bg-gray-100'}>{plan.health}</Badge>
                        <Badge variant="outline">{statusLabels[plan.status] || plan.status}</Badge>
                      </div>

                      {plan.customer_org_name && (
                        <p className="text-sm text-gray-500 mb-2">{plan.customer_org_name}</p>
                      )}

                      {/* Progress bar */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden max-w-xs">
                          <div
                            className="h-full bg-blue-600 rounded-full transition-all"
                            style={{ width: `${plan.progress_percent}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-600">
                          {plan.progress_percent}%
                        </span>
                        <span className="text-xs text-gray-400">
                          ({plan.completed_tasks}/{plan.total_tasks} tasks)
                        </span>
                      </div>

                      {/* Dates */}
                      <div className="flex gap-4 mt-2 text-xs text-gray-400">
                        {plan.start_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Started {new Date(plan.start_date).toLocaleDateString()}
                          </span>
                        )}
                        {plan.target_go_live_date && (
                          <span className="flex items-center gap-1">
                            <Target className="h-3 w-3" />
                            Go-live {new Date(plan.target_go_live_date).toLocaleDateString()}
                          </span>
                        )}
                        {plan.csm_name && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            CSM: {plan.csm_name}
                          </span>
                        )}
                      </div>
                    </div>

                    <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
